if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');

        this.isFeaturedProduct = this.closest('.section-featured-product');

        this.cleanHiddenProductInfoContainers = this.cleanHiddenProductInfoContainers.bind(this);
        document.addEventListener('DOMContentLoaded', this.cleanHiddenProductInfoContainers);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // QG 2026-09-24: after the renderProductInfo full-page fallback the address bar carries
        // `?option_values=<ids>`. Rewrite it to the canonical, shareable `?variant=<id>` for the
        // variant the server actually selected — the same URL a successful in-page switch
        // produces (updateURL). Main product only; never on quick-add or featured sections.
        if (this.dataset.updateUrl !== 'false' && new URLSearchParams(window.location.search).has('option_values')) {
          const selected = this.getSelectedVariant(this);
          if (selected?.id) this.updateURL(this.dataset.url, selected.id);
        }

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // If the DOM is already loaded by the time the component is added to the DOM
        if (document.readyState === 'interactive' || document.readyState === 'complete') {
          this.cleanHiddenProductInfoContainers();
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();

        document.removeEventListener('DOMContentLoaded', this.cleanHiddenProductInfoContainers);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
          // QG 2026-09-24: the server-rendered page for the same selection, used only when
          // the section request fails twice (see renderProductInfo).
          fallbackUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, true),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        // Remove any opened PDP modals
        document.querySelectorAll('.popup-modal--pdp').forEach((modal) => modal.remove());

        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, fallbackUrl, attempt = 1 }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        // Soft disable dropdowns
        this.querySelectorAll('variant-selects .nice__select').forEach(select => {
          select.classList.add('disabled');
        });

        // QG 2026-09-24 (owner goal: fix any new defect found in testing). The section request
        // below was trusted blindly: `response.text()` on a 404 is an empty string, which parses
        // to an empty document, `getSelectedVariant()` finds no variant, and handleUpdateProductInfo
        // calls setUnavailable() — telling the shopper an IN-STOCK variant is "Unavailable",
        // clearing the variant id and hiding the price. Measured on the dev preview: selecting
        // 50ft on the Cat 7 PDP (50ft / Black is available) failed this way in 3 of 6 fresh
        // sessions, each time on a `?section_id=…&option_values=…` 404 with a 0-byte body.
        //
        // A failed or empty render is now never treated as an answer about the variant:
        //   1. accept a response only if it is OK and actually contains the product markup;
        //   2. otherwise retry once;
        //   3. if the retry also fails, load the server-rendered page for the same selection
        //      (`fallbackUrl`, same option_values, no section_id) — always truthful.
        // A genuine "no such combination" still arrives as a valid page without a selected
        // variant, so real unavailability is reported exactly as before.
        const isRenderFailure = (error) => error && error.qgRenderFailure === true;
        // Review 2 F3: while a retry is in flight the dropdowns stay disabled; only the
        // attempt that actually finishes re-enables them.
        let handedOff = false;
        // Review 2 F4: never navigate away from a page that is not this product's own page —
        // quick-add modals and featured-product sections render product-info with
        // data-update-url="false". There, show an honest error and keep add-to-cart off.
        const mayNavigate = this.dataset.updateUrl !== 'false' && !this.closest('quick-add-modal, .quick-add-modal');

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw Object.assign(new Error(`Variant render HTTP ${response.status}`), { qgRenderFailure: true });
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!html.querySelector('product-info, variant-selects')) {
              throw Object.assign(new Error('Variant render returned no product markup'), { qgRenderFailure: true });
            }
            this.pendingRequestUrl = null;
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else if (isRenderFailure(error) && attempt === 1) {
              console.warn(`${error.message}; retrying once`);
              handedOff = true;
              this.renderProductInfo({ requestUrl, targetId, callback, fallbackUrl, attempt: 2 });
            } else if (isRenderFailure(error) && fallbackUrl && mayNavigate) {
              console.warn(`${error.message}; loading the full page for this selection`);
              handedOff = true;
              window.location.assign(fallbackUrl);
            } else if (isRenderFailure(error)) {
              // No safe navigation available (quick-add / featured product): keep add-to-cart
              // disabled — the form still holds the PREVIOUS variant, so enabling it would add
              // the wrong one — and say plainly what happened instead of "Unavailable".
              console.warn(`${error.message}; showing an error instead of navigating`);
              this.productForm?.toggleSubmitButton(true);
              // The `t` filter HTML-escapes (measured on hosted: "couldn&#39;t"), and
              // handleErrorMessage writes textContent, which would show the entity literally.
              // Decode via a <textarea>, which only decodes entities and never parses markup.
              const decoder = document.createElement('textarea');
              decoder.innerHTML = window.variantStrings.loadError || '';
              this.productForm?.handleErrorMessage(decoder.value);
            } else {
              console.error(error);
            }
          }).finally(() => {
            if (handedOff) return;
            // Soft enable dropdowns
            this.querySelectorAll('variant-selects .nice__select').forEach(select => {
              select.classList.remove('disabled');
            });
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);


          // QG 2026-09-24 — WRONG-VARIANT DEFECT, found on the hosted theme. `pickup-availability`
          // is a lazy-script (snippets/buy-buttons.liquid:231, loaded when it scrolls into view),
          // so the ELEMENT exists before its class is defined. `?.` guards only null/undefined,
          // not a missing method, so `this.pickupAvailability?.update(variant)` threw
          // "update is not a function" on the FIRST line of this callback — and every line below
          // it (variant id, SKU, price, ATC state) never ran. Measured on hosted theme
          // #159809110202: 6 of 6 option changes left the form on 1ft / Black while the picker
          // showed 50ft/3ft/10ft/25ft, i.e. the shopper would add the WRONG variant.
          //
          // Upgraded element: call update() as before. Not yet upgraded: record the selection on
          // the element instead — its init() fetches `data-variant-id` when the script arrives,
          // so it will show pickup for the variant actually chosen.
          //
          // Review 2 F1/F2: record the selection on the element in EVERY case — `update()` itself
          // never writes data-variant-id, so an element that is defined but has not run init()
          // yet would otherwise fetch the ORIGINAL variant later. `available` is set both ways,
          // including for a non-existent combination (variant null) and for a return to an
          // available variant after an unavailable one.
          const pickup = this.pickupAvailability;
          if (pickup) {
            if (variant?.id) pickup.dataset.variantId = variant.id;
            pickup.toggleAttribute('available', !!variant?.available);
            if (typeof pickup.update === 'function') pickup.update(variant);
          }
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          this.updateVariantDataVisibility(variant?.id);

          if (!variant) {
            this.querySelector(`#QgDelivery-${this.dataset.section}`)?.classList.add('hidden');
            this.querySelector(`#QgShipBadge-${this.dataset.section}`)?.classList.add('hidden');
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML.replace(/<template(?![^>]*\bdata-prevent-interpolation\b)[^>]*>[\s\S]*?<\/template>/g, '');;
              destination.classList.toggle('hidden', shouldHide(source));
              destination.classList.toggle('visibility-hidden', shouldHide(source));
            }
          };

          // P02: toggle only the `hidden` class so disclosure bindings on the badge stay intact.
          const updateHiddenFromSource = (id) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) destination.classList.toggle('hidden', source.classList.contains('hidden'));
          };
          updateHiddenFromSource('QgDelivery');
          updateHiddenFromSource('QgShipBadge');

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          updateSourceFromDestination('back-in-stock-enquiry');
          updateSourceFromDestination('product-enquiry');
          updateSourceFromDestination('Barcode');
          if (!this.isFeaturedProduct) updateSourceFromDestination('sticky-cta');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          window.renderSelects();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const shareButton = this.querySelector('share-button');
        // Review 2 F8: same failure class as pickup-availability above. updateURL() runs BEFORE
        // updateVariantInputs() in handleUpdateProductInfo, so a not-yet-defined share-button
        // throwing here would again leave the form on the previous variant. Dormant today (no
        // template renders share-button inside product-info), guarded so it stays harmless.
        if (shareButton && typeof shareButton.init === 'function' && typeof shareButton.updateUrl === 'function') {
          shareButton.init();
          shareButton.updateUrl(
            `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
          );
        }

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));

        this.querySelector('.product')?.setAttribute('data-variant-status', 'unavailable');
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden', 'spinning-complete');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden', 'spinning-complete'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      cleanHiddenProductInfoContainers() {
        if (!Shopify.designMode) {
          document.querySelectorAll('.product__info-container[hidden]').forEach((element) => {
            element.remove();
          });
        }
      }

      updateVariantDataVisibility(variantId) {
        document.querySelectorAll(`[data-variant-data-product-id='${this.dataset.productId}']`).forEach(dataPiece => {
          dataPiece.classList.toggle('hidden', dataPiece.dataset.variantDataVariantId != variantId);
        });
      }


      get productForm() {
        const productForm = this.querySelector('.product-form--buy-buttons');
        if (productForm) {
          productForm.init();
        }
        return productForm;
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
